// Conversion between the amount shown to the user and the percentage stored.
//
// The round trip is not neutral at two decimals: 9.03% gives 13.4218…
// rounded to 13.42, and 13.42 re-entered gives back 9.0171%. We therefore
// store four decimals, display two, and only recompute the percentage IF the
// amount was actually edited — an untouched row keeps its value bit for bit.

export const PERCENTAGE_PRECISION = 4;

export const amountFromPercentage = (minimumWage, percentage) => {
  const wage = parseFloat(minimumWage);
  if (!Number.isFinite(wage) || !Number.isFinite(percentage)) return null;
  return parseFloat((wage * (1 + percentage / 100)).toFixed(2));
};

export const percentageFromAmount = (minimumWage, amount) => {
  const wage = parseFloat(minimumWage);
  const target = parseFloat(amount);
  if (!Number.isFinite(wage) || !Number.isFinite(target) || wage === 0) return null;
  return parseFloat(((target / wage - 1) * 100).toFixed(PERCENTAGE_PRECISION));
};

export const formatPercentage = (percentage) =>
  Number.isFinite(percentage) ? `+${percentage.toFixed(2)}%` : '';

export const sortRanges = (ranges) =>
  [...ranges].sort((a, b) => (a.percentage ?? Infinity) - (b.percentage ?? Infinity));

// Nothing is stored to say "this company is still on the default values", and
// since creation persists the real template a company can no longer be
// recognised by its null coefficients alone. Compare against the template the
// back sends along (companyInfo.defaultCoefficients) instead: same number of
// ranges, and the same percentage and both coefficients on each of them, both
// sides sorted by percentage so a re-ordered save does not read as a change.
// A different range count means the operator added or removed a range — the
// company has deviated from the template, so it is not on default values.
export const isDefaultCoefficientRanges = (ranges, template) => {
  const rows = ranges ?? [];
  // Legacy rows predating the template carry no coefficient at all — either
  // nulled by getCompanyInfo or simply absent from the document: never
  // configured, hence still on default values.
  const isUnset = (value) => value === null || value === undefined;
  if (rows.length === 0) return true;
  if (rows.every((row) => isUnset(row.delegation) && isUnset(row.management)))
    return true;

  const defaults = template ?? [];
  if (defaults.length === 0 || rows.length !== defaults.length) return false;

  const sortedRows = sortRanges(rows);
  const sortedDefaults = sortRanges(defaults);
  return sortedRows.every(
    (row, index) =>
      row.percentage === sortedDefaults[index].percentage &&
      row.delegation === sortedDefaults[index].delegation &&
      row.management === sortedDefaults[index].management
  );
};
